import React, {useRef, useEffect} from 'react'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import useAuth from '../../../../hooks/useAuth'
import {AuthFullScreen} from './AuthFullScreen'

export function ForgotPassword() {
  const intl = useIntl()
  const {requestResetPassword, actionLoading} = useAuth()
  const [error, setError] = React.useState<string | null>(null)
  const [emailSent, setEmailSent] = React.useState(false)
  const hasSubmittedRef = useRef(false)

  const schema = Yup.object().shape({
    username: Yup.string()
      .email(intl.formatMessage({id: 'FORM.ERROR.EMAIL.WRONG_FORMAT'}))
      .min(3, intl.formatMessage({id: 'FORM.ERROR.SYMBOL.MIN'}, {min: 3}))
      .max(50, intl.formatMessage({id: 'FORM.ERROR.SYMBOL.MAX'}, {max: 50}))
      .required(intl.formatMessage({id: 'FORM.ERROR.REQUIRED'})),
  })

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (actionLoading) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [actionLoading])

  const formik = useFormik({
    initialValues: {username: ''},
    validationSchema: schema,
    onSubmit: async (values, {resetForm}) => {
      if (hasSubmittedRef.current) return
      hasSubmittedRef.current = true
      setError(null)
      try {
        const action: any = await requestResetPassword(values)
        const payload = action?.payload
        if (payload?.success) {
          setEmailSent(true)
          resetForm()
        } else {
          setError(intl.formatMessage({id: 'ERROR.SENT_PASSWORD_RESET'}))
          hasSubmittedRef.current = false
        }
      } catch {
        setError(intl.formatMessage({id: 'ERROR.SENT_PASSWORD_RESET'}))
        hasSubmittedRef.current = false
      }
    },
  })

  const submitting = actionLoading || formik.isSubmitting

  return (
    <AuthFullScreen>
      <div className='marefati-login'>
          <div className='marefati-login__header'>
            <div className='marefati-login__secure'>
              <span className='marefati-login__lock'>
                <i className='bi bi-shield-lock-fill' />
              </span>
              <span className='marefati-login__secure-text'>Espace sécurisé Maarefati</span>
            </div>
          </div>

          <div className='marefati-login__title'>
            <h1>{intl.formatMessage({id: 'MENU.FORGOT_PASSWORD'})}</h1>
            <p>{intl.formatMessage({id: 'MENU.ENTER_EMAIL_TO_RESET_PASSWORD'})}</p>
          </div>

          {emailSent ? (
            <div className='marefati-login__form'>
              <div className='marefati-login__success'>
                <i className='bi bi-envelope-check-fill' />
                <p>{intl.formatMessage({id: 'SUCCESS.SENT_PASSWORD_RESET'})}</p>
              </div>
              <Link to='/auth/login' className='marefati-login__submit marefati-login__submit--link'>
                {intl.formatMessage({id: 'MENU.LOGIN'})}
              </Link>
            </div>
          ) : (
            <form className='marefati-login__form' onSubmit={formik.handleSubmit} noValidate>
              <div className='marefati-login__field'>
                <label className='marefati-login__label'>
                  {intl.formatMessage({id: 'INPUT.EMAIL.LABEL'})}
                </label>
                <input
                  type='email'
                  {...formik.getFieldProps('username')}
                  className={
                    'marefati-login__input' +
                    (formik.touched.username && formik.errors.username
                      ? ' marefati-login__input--error'
                      : '')
                  }
                  placeholder={intl.formatMessage({id: 'INPUT.EMAIL.PLACEHOLDER'})}
                  autoComplete='email'
                />
                {formik.touched.username && formik.errors.username && (
                  <div className='marefati-login__error'>{formik.errors.username}</div>
                )}
              </div>

              {error && <div className='marefati-login__status'>{error}</div>}

              <div className='marefati-login__actions'>
                <Link to='/auth/login' className='marefati-login__secondary-btn'>
                  {intl.formatMessage({id: 'MENU.CANCEL'})}
                </Link>
                <button
                  type='submit'
                  disabled={submitting || !formik.isValid}
                  className='marefati-login__submit'
                >
                  {submitting && <span className='spinner-border spinner-border-sm me-2' />}
                  {intl.formatMessage({id: 'MENU.SUBMIT'})}
                </button>
              </div>
            </form>
          )}

          <div className='marefati-login__meta'>
            <span className='marefati-login__version'>v1.0.0</span>
            <span className='marefati-login__copy'>
              © {new Date().getFullYear()} Maarefati
            </span>
          </div>
        </div>
    </AuthFullScreen>
  )
}
